// ═══════════════════════════════════════════════════════════════
//  怪物图鉴（20 种）—— 数据驱动
//
//  设计参考现代 Roguelike / 竞技场射击游戏的敌人设计：
//    Tier 1  基础近战      —— 追人、接触伤害（学节奏、可被遛）
//    Tier 2  冲锋 / 飞行    —— 冲刺突进、空中单位（穿墙）
//    Tier 3  远程 / 弹药    —— 弹幕压力、抛物线爆破、反弹甲壳
//    Tier 4  精英          —— 高威胁：追踪弹、自爆、重装突进
//    Tier 5  BOSS          —— 多段技能：弹幕 + 冲锋
//
//  攻击样式（AttackStyle）：
//    melee   接触近战
//    charge  蓄力冲刺（冲刺中撞击伤害更高）
//    ranged  保持距离的直线弹
//    spread  扇形多发（霰弹）
//    burst   连发点射
//    lob     抛物线炸弹（落地爆炸）
//    homing  追踪弹
//    explode 自爆（接近后引爆，范围伤害）
//    spike   甲壳反弹（被撞时反伤玩家）
//    summon  召唤小怪
//
//  飞行单位（flying）可穿越墙壁。
// ═══════════════════════════════════════════════════════════════

export type AttackStyle =
  | 'melee' | 'charge' | 'ranged' | 'spread' | 'burst'
  | 'lob' | 'homing' | 'explode' | 'spike' | 'summon';

export type Tier = 1 | 2 | 3 | 4 | 5;

/** 一张动画序列图（spritesheet） */
export interface SheetSpec {
  /** 纹理 key（也是动画名的前缀） */
  key: string;
  /** assets 下的相对路径 */
  file: string;
  fw: number;
  fh: number;
}

export interface EnemyDef {
  id: string;
  nameKey: string;
  tier: Tier;
  style: AttackStyle;
  flying?: boolean;

  /** 动画序列图 */
  sheets: SheetSpec[];
  /** 各部件的纹理 key */
  anims: { idle: string; run?: string; attack?: string; hit?: string };

  /** 显示缩放（原始素材较大） */
  scale: number;
  /** 碰撞体尺寸 */
  body: { w: number; h: number };
  /** 贴图显示尺寸（px）；不填则用原始 */
  display?: { w: number; h: number };

  hp: number;
  speed: number;
  damage: number;
  attackRange: number;   // 攻击/射击距离
  attackRate: number;    // 攻击间隔 ms
  tint?: number;

  /** 样式专属参数 */
  params?: {
    keepMin?: number;      // ranged: 后退距离
    keepMax?: number;      // ranged: 前压距离
    bulletSpeed?: number;
    bulletLife?: number;
    count?: number;        // spread/burst/summon 数量
    arcDeg?: number;       // spread 扇形角度
    interval?: number;     // burst 内间隔
    dashMs?: number;       // charge 冲刺时长
    dashMul?: number;      // charge 冲刺速度倍率
    detect?: number;       // charge 察觉距离
    fuseMs?: number;       // explode 引信时间
    radius?: number;       // explode/lob 爆炸半径
    minion?: string;       // summon 召唤的怪 id
  };
}

const S = (key: string, file: string, fw: number, fh: number): SheetSpec => ({ key, file, fw, fh });

export const ENEMIES: EnemyDef[] = [
  // ── Tier 1：基础近战 ───────────────────────────────────────
  {
    id: 'slime', nameKey: 'enemies.slime', tier: 1, style: 'melee',
    sheets: [S('slime_idle', 'monsters/slime_idle.png', 44, 30), S('slime_hit', 'monsters/slime_hit.png', 44, 30)],
    anims: { idle: 'slime_idle', hit: 'slime_hit' },
    scale: 0.75, body: { w: 30, h: 20 }, display: { w: 33, h: 22 },
    hp: 60, speed: 40, damage: 10, attackRange: 24, attackRate: 1000,
  },
  {
    id: 'radish', nameKey: 'enemies.radish', tier: 1, style: 'melee',
    sheets: [S('radish_idle', 'monsters/radish_idle.png', 30, 38), S('radish_run', 'monsters/radish_run.png', 30, 38), S('radish_hit', 'monsters/radish_hit.png', 30, 38)],
    anims: { idle: 'radish_idle', run: 'radish_run', hit: 'radish_hit' },
    scale: 0.8, body: { w: 18, h: 26 },
    hp: 35, speed: 55, damage: 9, attackRange: 24, attackRate: 1000,
  },
  {
    id: 'chicken', nameKey: 'enemies.chicken', tier: 1, style: 'melee',
    sheets: [S('chicken_idle', 'monsters/chicken_idle.png', 32, 34), S('chicken_run', 'monsters/chicken_run.png', 32, 34), S('chicken_hit', 'monsters/chicken_hit.png', 32, 34)],
    anims: { idle: 'chicken_idle', run: 'chicken_run', hit: 'chicken_hit' },
    scale: 0.8, body: { w: 18, h: 22 },
    hp: 25, speed: 78, damage: 7, attackRange: 22, attackRate: 900,
  },
  {
    id: 'snail', nameKey: 'enemies.snail', tier: 1, style: 'melee',
    sheets: [S('snail_idle', 'monsters/snail_idle.png', 38, 24), S('snail_run', 'monsters/snail_run.png', 38, 24), S('snail_hit', 'monsters/snail_hit.png', 38, 24)],
    anims: { idle: 'snail_idle', run: 'snail_run', hit: 'snail_hit' },
    scale: 0.9, body: { w: 26, h: 16 },
    hp: 85, speed: 26, damage: 13, attackRange: 24, attackRate: 1200,
  },

  // ── Tier 2：冲锋 / 飞行 / 召唤 ─────────────────────────────
  {
    id: 'angrypig', nameKey: 'enemies.angrypig', tier: 2, style: 'charge',
    sheets: [S('angrypig_idle', 'monsters/angrypig_idle.png', 36, 30), S('angrypig_run', 'monsters/angrypig_run.png', 36, 30), S('angrypig_hit', 'monsters/angrypig_hit.png', 36, 30)],
    anims: { idle: 'angrypig_idle', run: 'angrypig_run', hit: 'angrypig_hit' },
    scale: 0.85, body: { w: 24, h: 18 },
    hp: 45, speed: 60, damage: 14, attackRange: 26, attackRate: 900,
    params: { detect: 260, dashMs: 420, dashMul: 3.0, keepMin: 120 },
  },
  {
    id: 'bunny', nameKey: 'enemies.bunny', tier: 2, style: 'charge',
    sheets: [S('bunny_idle', 'monsters/bunny_idle.png', 34, 44), S('bunny_run', 'monsters/bunny_run.png', 34, 44), S('bunny_hit', 'monsters/bunny_hit.png', 34, 44)],
    anims: { idle: 'bunny_idle', run: 'bunny_run', hit: 'bunny_hit' },
    scale: 0.7, body: { w: 18, h: 28 },
    hp: 30, speed: 70, damage: 10, attackRange: 26, attackRate: 800,
    params: { detect: 300, dashMs: 300, dashMul: 3.4, keepMin: 140 },
  },
  {
    id: 'bat', nameKey: 'enemies.bat', tier: 2, style: 'melee', flying: true,
    sheets: [S('bat_idle', 'monsters/bat_idle.png', 46, 30), S('bat_run', 'monsters/bat_run.png', 46, 30), S('bat_hit', 'monsters/bat_hit.png', 46, 30)],
    anims: { idle: 'bat_idle', run: 'bat_run', hit: 'bat_hit' },
    scale: 0.7, body: { w: 26, h: 16 },
    hp: 20, speed: 92, damage: 8, attackRange: 24, attackRate: 900,
  },
  {
    id: 'bluebird', nameKey: 'enemies.bluebird', tier: 2, style: 'charge', flying: true,
    sheets: [S('bluebird_idle', 'monsters/bluebird_idle.png', 32, 32), S('bluebird_hit', 'monsters/bluebird_hit.png', 32, 32)],
    anims: { idle: 'bluebird_idle', hit: 'bluebird_hit' },
    scale: 0.85, body: { w: 20, h: 16 },
    hp: 22, speed: 95, damage: 9, attackRange: 26, attackRate: 800,
    params: { detect: 320, dashMs: 320, dashMul: 3.2, keepMin: 150 },
  },
  {
    id: 'mushroom', nameKey: 'enemies.mushroom', tier: 2, style: 'summon',
    sheets: [S('mushroom_idle', 'monsters/mushroom_idle.png', 32, 32), S('mushroom_run', 'monsters/mushroom_run.png', 32, 32), S('mushroom_hit', 'monsters/mushroom_hit.png', 32, 32)],
    anims: { idle: 'mushroom_idle', run: 'mushroom_run', hit: 'mushroom_hit' },
    scale: 0.85, body: { w: 20, h: 20 },
    hp: 70, speed: 34, damage: 8, attackRange: 200, attackRate: 1000,
    params: { keepMin: 150, keepMax: 260, count: 1, interval: 5000, minion: 'chicken' },
  },

  // ── Tier 3：远程 / 爆破 / 甲壳 ─────────────────────────────
  {
    id: 'bee', nameKey: 'enemies.bee', tier: 3, style: 'spread', flying: true,
    sheets: [S('bee_idle', 'monsters/bee_idle.png', 36, 34), S('bee_attack', 'monsters/bee_attack.png', 36, 34), S('bee_hit', 'monsters/bee_hit.png', 36, 34)],
    anims: { idle: 'bee_idle', attack: 'bee_attack', hit: 'bee_hit' },
    scale: 0.85, body: { w: 22, h: 18 },
    hp: 28, speed: 80, damage: 8, attackRange: 300, attackRate: 1600,
    params: { keepMin: 180, keepMax: 300, bulletSpeed: 200, bulletLife: 2600, count: 3, arcDeg: 26 },
  },
  {
    id: 'plant', nameKey: 'enemies.plant', tier: 3, style: 'ranged',
    sheets: [S('plant_idle', 'monsters/plant_idle.png', 44, 42), S('plant_attack', 'monsters/plant_attack.png', 44, 42), S('plant_hit', 'monsters/plant_hit.png', 44, 42)],
    anims: { idle: 'plant_idle', attack: 'plant_attack', hit: 'plant_hit' },
    scale: 0.8, body: { w: 32, h: 32 },
    hp: 60, speed: 0, damage: 12, attackRange: 360, attackRate: 1500,
    params: { keepMin: 0, keepMax: 0, bulletSpeed: 250, bulletLife: 3000 },
  },
  {
    id: 'trunk', nameKey: 'enemies.trunk', tier: 3, style: 'burst',
    sheets: [S('trunk_idle', 'monsters/trunk_idle.png', 64, 32), S('trunk_run', 'monsters/trunk_run.png', 64, 32), S('trunk_attack', 'monsters/trunk_attack.png', 64, 32), S('trunk_hit', 'monsters/trunk_hit.png', 64, 32)],
    anims: { idle: 'trunk_idle', run: 'trunk_run', attack: 'trunk_attack', hit: 'trunk_hit' },
    scale: 0.6, body: { w: 44, h: 26 },
    hp: 75, speed: 30, damage: 13, attackRange: 320, attackRate: 2200,
    params: { keepMin: 160, keepMax: 300, bulletSpeed: 260, bulletLife: 2600, count: 3, interval: 180 },
  },
  {
    id: 'duck', nameKey: 'enemies.duck', tier: 3, style: 'lob',
    sheets: [S('duck_idle', 'monsters/duck_idle.png', 36, 36), S('duck_hit', 'monsters/duck_hit.png', 36, 36)],
    anims: { idle: 'duck_idle', hit: 'duck_hit' },
    scale: 0.85, body: { w: 20, h: 20 },
    hp: 40, speed: 46, damage: 16, attackRange: 280, attackRate: 2200,
    params: { keepMin: 140, keepMax: 260, bulletSpeed: 240, count: 1, radius: 62 },
  },
  {
    id: 'turtle', nameKey: 'enemies.turtle', tier: 3, style: 'spike',
    sheets: [S('turtle_idle', 'monsters/turtle_idle.png', 44, 26), S('turtle_attack', 'monsters/turtle_attack.png', 44, 26), S('turtle_hit', 'monsters/turtle_hit.png', 44, 26)],
    anims: { idle: 'turtle_idle', attack: 'turtle_attack', hit: 'turtle_hit' },
    scale: 0.9, body: { w: 32, h: 18 },
    hp: 110, speed: 32, damage: 16, attackRange: 26, attackRate: 1100,
    params: { keepMin: 0 },
  },

  // ── Tier 4：精英 ───────────────────────────────────────────
  {
    id: 'rocks', nameKey: 'enemies.rocks', tier: 4, style: 'charge',
    sheets: [S('rocks_idle', 'monsters/rocks_idle.png', 38, 34), S('rocks_run', 'monsters/rocks_run.png', 38, 34), S('rocks_hit', 'monsters/rocks_hit.png', 38, 34)],
    anims: { idle: 'rocks_idle', run: 'rocks_run', hit: 'rocks_hit' },
    scale: 1, body: { w: 28, h: 24 },
    hp: 140, speed: 55, damage: 18, attackRange: 30, attackRate: 1000,
    params: { detect: 340, dashMs: 620, dashMul: 2.6, keepMin: 180 },
  },
  {
    id: 'ghost', nameKey: 'enemies.ghost', tier: 4, style: 'melee', flying: true,
    sheets: [S('ghost_idle', 'monsters/ghost_idle.png', 44, 30), S('ghost_hit', 'monsters/ghost_hit.png', 44, 31)],
    anims: { idle: 'ghost_idle', hit: 'ghost_hit' },
    scale: 0.85, body: { w: 30, h: 20 }, tint: 0xbfe3ff,
    hp: 55, speed: 58, damage: 12, attackRange: 26, attackRate: 1000,
  },
  {
    id: 'skull', nameKey: 'enemies.skull', tier: 4, style: 'homing', flying: true,
    sheets: [S('skull_idle', 'monsters/skull_idle.png', 52, 54), S('skull_hit', 'monsters/skull_hit.png', 52, 54)],
    anims: { idle: 'skull_idle', hit: 'skull_hit' },
    scale: 0.6, body: { w: 30, h: 32 },
    hp: 70, speed: 52, damage: 15, attackRange: 340, attackRate: 2400,
    params: { keepMin: 200, keepMax: 340, bulletSpeed: 150, bulletLife: 4200 },
  },
  {
    id: 'fatbird', nameKey: 'enemies.fatbird', tier: 4, style: 'explode', flying: true,
    sheets: [S('fatbird_idle', 'monsters/fatbird_idle.png', 40, 48), S('fatbird_run', 'monsters/fatbird_run.png', 40, 48), S('fatbird_hit', 'monsters/fatbird_hit.png', 40, 48)],
    anims: { idle: 'fatbird_idle', run: 'fatbird_run', hit: 'fatbird_hit' },
    scale: 0.75, body: { w: 26, h: 30 }, tint: 0xffd0a0,
    hp: 65, speed: 62, damage: 22, attackRange: 34, attackRate: 1000,
    params: { detect: 120, fuseMs: 780, radius: 84 },
  },
  {
    id: 'rino', nameKey: 'enemies.rino', tier: 4, style: 'charge',
    sheets: [S('rino_idle', 'monsters/rino_idle.png', 52, 34), S('rino_run', 'monsters/rino_run.png', 52, 34), S('rino_hit', 'monsters/rino_hit.png', 52, 34)],
    anims: { idle: 'rino_idle', run: 'rino_run', hit: 'rino_hit' },
    scale: 0.75, body: { w: 40, h: 24 },
    hp: 130, speed: 62, damage: 22, attackRange: 30, attackRate: 1100,
    params: { detect: 420, dashMs: 700, dashMul: 2.9, keepMin: 220 },
  },

  // ── Tier 5：BOSS ──────────────────────────────────────────
  {
    id: 'chameleon', nameKey: 'enemies.chameleon', tier: 5, style: 'spread',
    sheets: [S('chameleon_idle', 'monsters/chameleon_idle.png', 84, 38), S('chameleon_run', 'monsters/chameleon_run.png', 84, 38), S('chameleon_attack', 'monsters/chameleon_attack.png', 84, 38), S('chameleon_hit', 'monsters/chameleon_hit.png', 84, 38)],
    anims: { idle: 'chameleon_idle', run: 'chameleon_run', attack: 'chameleon_attack', hit: 'chameleon_hit' },
    scale: 0.8, body: { w: 60, h: 28 },
    hp: 260, speed: 50, damage: 20, attackRange: 340, attackRate: 1800,
    params: { keepMin: 200, keepMax: 340, bulletSpeed: 230, bulletLife: 3000, count: 5, arcDeg: 46 },
  },
];

export const ENEMY_BY_ID: Record<string, EnemyDef> = Object.fromEntries(
  ENEMIES.map((e) => [e.id, e])
);

export function getEnemy(id: string): EnemyDef {
  return ENEMY_BY_ID[id] ?? ENEMY_BY_ID.slime;
}

/** 某层可出现的怪物（按 tier 解锁；BOSS 单独处理） */
export function enemiesForFloor(floor: number): EnemyDef[] {
  const maxTier: Tier = floor >= 15 ? 4 : floor >= 8 ? 3 : floor >= 4 ? 2 : 1;
  return ENEMIES.filter((e) => e.tier <= maxTier && e.tier <= 4);
}

/** 掷骰选一种普通怪（越深的层越容易出高 tier） */
export function pickEnemyForFloor(floor: number): EnemyDef {
  const pool = enemiesForFloor(floor);
  // 权重：tier 越接近当前解锁上限，权重越高
  const maxTier = Math.max(...pool.map((e) => e.tier));
  const weighted: EnemyDef[] = [];
  pool.forEach((e) => {
    const w = Math.max(1, Math.round((e.tier / maxTier) * 3));
    for (let i = 0; i < w; i++) weighted.push(e);
  });
  return weighted[Math.floor(Math.random() * weighted.length)] ?? ENEMY_BY_ID.slime;
}

export const BOSS_ENEMY = ENEMY_BY_ID.chameleon;
